/* Só o que é DO DOCUMENTO. Célula, camada, raio concêntrico, campos de cor,
 * rótulo, cifra e as regras de impressão da célula já vêm do sistema
 * (`design/globals.css`); repetir aqui seria criar um segundo design system.
 * Fica neste arquivo o que só o laudo tem: a numeração de capítulos, a grade
 * de premissas, a tabela dos doze meses, o traço dos gráficos e o preparo de
 * página para o PDF.
 *
 * Nenhuma cor, tamanho de tipo ou raio literal: tudo sai de
 * `design/tokens.css`. Medida de grade e geometria de SVG são deste documento
 * e não têm token. */
export const CSS = `
.laudo{
  max-width: var(--largura-laudo);
  margin-inline: auto;
  display: grid;
  gap: var(--e5);
  counter-reset: capitulo;
}
.laudo > *{ min-width: 0 }
/* Duas ações de tela, não uma barra de formulário: ficam juntas à direita.
   Espalhadas pelos 870px da folha viravam dois botões sem relação entre si. */
.laudo .acoes{ display:flex; flex-wrap:wrap; gap: var(--e3); justify-content: flex-end }

/* O parágrafo do laudo. Não é .apoio: aqui o texto é LEITURA CORRIDA, o
   leitor lê do começo ao fim, e 70ch é a medida disso. O .apoio fecha em 52ch
   porque apoio se lê em diagonal. A decisão aparecia seis vezes escrita à mão
   no JSX, com a entrelinha vindo da escala do Tailwind em vez do token. */
.laudo .prosa{
  max-width: var(--largura-leitura);
  font-size: var(--t-apoio);
  line-height: var(--altura-solta);
  color: var(--tinta-2);
}

/* ── Capa ───────────────────────────────────────────────────────────────
   O único bloco sem número de capítulo e o único com fio de 2px: é ele que
   abre o documento, e o veredito vem dentro dele. Daí a folga extra antes do
   capítulo 01 — sem esse degrau, capa e capítulo pesavam igual e a página
   inteira lia como uma pilha de blocos sem começo. */
.laudo .capa{ display:grid; gap: var(--e4); margin-bottom: var(--e3) }
.laudo .capa-topo{
  display:flex; flex-wrap:wrap; align-items:flex-end; justify-content:space-between;
  gap: var(--e3); padding-bottom: var(--e3); border-bottom: 2px solid var(--marca-mata);
}
/* O selo do veredito é curto em toda tela do produto, menos aqui: no laudo
   ele é uma frase ("Meta inviável com os percentuais atuais"). Nowrap nela
   estoura a largura do celular. */
.laudo .capa-topo [data-slot='selo-estado']{ white-space: normal }
/* A ficha de identificação é uma faixa fechada, não itens soltos boiando
   entre o título e o veredito. */
.laudo .identificacao{
  display:flex; flex-wrap:wrap; gap: var(--e4) var(--e7);
  padding-bottom: var(--e4); border-bottom: 1px solid var(--fio);
}
/* Aqui o VALOR é que manda, e o rótulo só nomeia: invertido (rótulo em 600 e
   valor em 500) o olho lia primeiro "Empresa" e depois o nome da empresa. */
.laudo .identificacao dt{ font-size: var(--t-nota); color: var(--tinta-2) }
.laudo .identificacao dd{
  font-size: var(--t-apoio); font-weight: var(--peso-forte); color: var(--tinta); margin-top: 2px;
}

/* ── Capítulos ──────────────────────────────────────────────────────────
   O título de capítulo era mono, 12px, CAIXA ALTA e tinta-3: uma etiqueta
   mais fraca que o texto que ela encabeçava, e num documento de seis blocos
   isso é o mesmo que não ter título. Agora é o degrau de título de cartão
   (20/600/tinta) e quem separa nível é tamanho e peso. Do desenho antigo
   sobra só o número, que continua mono porque é número. */
.laudo .secao{ break-inside: avoid; page-break-inside: avoid }
/* Tipo e cor vêm de .titulo-cartao, que o h2 também carrega: aqui fica só
   a geometria do documento (a régua e o número do capítulo). Redeclarar o
   degrau de título dentro do laudo criava um sistema paralelo, em que mexer
   na escala não alcançava o relatório. */
.laudo .titulo-secao{
  display:flex; align-items:baseline; gap: var(--e3);
  padding-bottom: var(--e2); margin-bottom: var(--e4); border-bottom: 1px solid var(--fio-forte);
}
.laudo .titulo-secao::before{
  counter-increment: capitulo;
  content: counter(capitulo, decimal-leading-zero);
  font-family: var(--fonte-num); font-size: var(--t-nota); font-weight: var(--peso-max);
  letter-spacing: 0; color: var(--acao);
}
/* Anexo: mesma família, um degrau abaixo do capítulo e sem número. É
   material de consulta, e o corpo do laudo tem de fechar sem ele. */
.laudo .anexo{ margin-top: var(--e3) }
.laudo .titulo-anexo{
  margin-top: var(--e1);
  padding-bottom: var(--e2); margin-bottom: var(--e4); border-bottom: 1px solid var(--fio);
}
/* A palavra "Anexo" faz o papel do número do capítulo, não o de um segundo
   título: em .rotulo ela era 13/600 colada num 16/600, dois degraus quase
   iguais empilhados, que é justamente o defeito que a escala nova corrigiu. */
.laudo .marca-anexo{
  font-family: var(--fonte-num); font-size: var(--t-nota);
  font-weight: var(--peso-max); color: var(--acao);
}
/* Quatro indicadores em 2×2. Com o mínimo em 212px a fileira caía em 3+1 na
   largura da folha, e o quarto esticava sozinho por toda a linha. */
.laudo .grade-indicadores{ grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr)) }

/* ── Premissas ──────────────────────────────────────────────────────────
   Par empilhado, não linha com rótulo numa ponta e valor na outra: numa
   coluna de 400px o olho perdia o par no meio do caminho. Empilhado, a
   coluna encolhe sem nunca separar os dois. */
.laudo .premissas{
  display:grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 232px), 1fr));
  gap: var(--e4);
}
.laudo .premissas > div{
  display:grid; gap: 2px; align-content:start; min-width: 0;
  border-bottom: 1px solid var(--fio); padding-bottom: var(--e2);
}
.laudo .premissas > div[data-largo]{ grid-column: 1 / -1 }
.laudo .premissas dt{ font-size: var(--t-nota); color: var(--tinta-2) }
.laudo .premissas dd{ font-size: var(--t-apoio); font-weight: var(--peso-forte); color: var(--tinta) }
/* Cifra não quebra no meio: "R$" numa linha e o valor na outra é ruído. */
.laudo .premissas dd.cifra{ white-space: nowrap }

/* ── Tabela dos doze meses ──────────────────────────────────────────── */
.laudo .tabela-meses{ overflow-x:auto; margin-top: var(--e5) }
.laudo .tabela-meses table{ width:100%; border-collapse: collapse; font-size: var(--t-apoio) }
.laudo .tabela-meses th, .laudo .tabela-meses td{ padding: var(--e2) var(--e3); white-space: nowrap }
/* Cabeçalho de coluna é rótulo, e rótulo neste sistema é 13/600 em caixa
   normal. Era mono a 11px em caixa alta: mais difícil de ler que os números
   que ele nomeia, e são doze linhas deles. */
.laudo .tabela-meses thead th{
  font-size: var(--t-apoio); font-weight: var(--peso-forte); color: var(--tinta-2);
  border-bottom: 1px solid var(--fio-forte); text-align: right;
}
.laudo .tabela-meses thead th:first-child{ text-align: left }
.laudo .tabela-meses tbody th{
  font-family: var(--fonte-num); font-weight: var(--peso-medio); color: var(--tinta-2); text-align: left;
}
.laudo .tabela-meses tbody td{ text-align: right }
.laudo .tabela-meses tbody tr{ border-bottom: 1px solid var(--fio) }
.laudo .tabela-meses tbody tr:last-child{ border-bottom: none }
.laudo .tabela-meses tbody tr[data-equilibrio]{ background: var(--mint) }
.laudo .tabela-meses tbody tr[data-equilibrio] th:first-child{
  border-top-left-radius: var(--r-campo); border-bottom-left-radius: var(--r-campo);
}
.laudo .tabela-meses tbody tr[data-equilibrio] td:last-child{
  border-top-right-radius: var(--r-campo); border-bottom-right-radius: var(--r-campo);
}

/* ── Gráficos ───────────────────────────────────────────────────────── */
.laudo .graficos{ display:grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 304px), 1fr)); gap: var(--e5) }
.laudo .grafico{ display:grid; gap: var(--e2); min-width:0; break-inside: avoid }
.laudo .svg{ width:100%; height:auto; display:block; overflow: visible }
.laudo .grade{ stroke: var(--fio); stroke-width: 1 }
.laudo .rotulo-eixo{ font-family: var(--fonte-num); font-size: var(--t-micro); fill: var(--tinta-3) }
/* O TRAÇO fica na cor da série; o RÓTULO, não. Em --cascata-3 este texto de
   11px dava cerca de 3:1 sobre a nata, e legenda ilegível é legenda que não
   existe. Quem identifica a linha é o tracejado, que continua na cor. */
.laudo .rotulo-limiar{
  font-family: var(--fonte-num); font-size: var(--t-micro);
  fill: var(--tinta-2); font-weight: var(--peso-forte);
}
.laudo .barra{ fill: var(--info) }
.laudo .linha-fixa{ stroke: var(--cascata-3); stroke-width: 1.5; stroke-dasharray: 6 4 }
.laudo .curva-atual{ fill:none; stroke: var(--tinta-2); stroke-width: 2; stroke-linejoin: round; stroke-linecap: round }
.laudo .curva-minimo{ fill:none; stroke: var(--acao); stroke-width: 2; stroke-linejoin: round; stroke-linecap: round }
.laudo .ponto-atual{ fill: var(--tinta-2); stroke: var(--nata); stroke-width: 2 }
.laudo .ponto-minimo{ fill: var(--acao); stroke: var(--nata); stroke-width: 2 }
.laudo .legenda{ display:flex; flex-wrap:wrap; gap: var(--e2) var(--e4); font-size: var(--t-nota); color: var(--tinta-2) }
.laudo .legenda span{ display:inline-flex; align-items:center; gap: var(--e2) }
.laudo .traco{ display:inline-block; width: var(--e4); height: 2px; border-radius: var(--r-pilula) }
.laudo .traco-atual{ background: var(--tinta-2) }
.laudo .traco-minimo{ background: var(--acao) }
.laudo .traco-fixa{ background: repeating-linear-gradient(90deg, var(--cascata-3) 0, var(--cascata-3) 6px, transparent 6px, transparent 10px) }

/* ── Recomendações ──────────────────────────────────────────────────── */
.laudo .opcoes{ grid-template-columns: repeat(auto-fit, minmax(min(100%, 284px), 1fr)); margin-top: var(--e4) }
.laudo .opcao{ display:grid; gap: var(--e2); align-content:start; border-left: 3px solid var(--fio-forte) }
/* A letra da opção é marcador de ordem, não título: fica abaixo do título de
   bloco em tamanho e acima dele em cor, que é o que um marcador faz. Como
   .rotulo ela pesava igual ao título e os dois brigavam. */
.laudo .opcao-letra{ font-size: var(--t-nota); font-weight: var(--peso-forte); color: var(--acao) }
.laudo .opcao-custo .opcao-letra{ color: var(--atencao) }
.laudo .opcao-preco{ border-left-color: var(--acao) }
.laudo .opcao-custo{ border-left-color: var(--atencao) }
.laudo .lista{ display:grid; gap: var(--e2); padding-left: var(--e4); font-size: var(--t-apoio); color: var(--tinta-2); line-height: var(--altura-solta) }
.laudo .lista li{ list-style: disc; max-width: var(--largura-leitura) }

/* ── Rodapé ─────────────────────────────────────────────────────────── */
.laudo .rodape{
  display:grid; gap: var(--e1); border-top: 1px solid var(--fio); padding-top: var(--e3);
  font-size: var(--t-nota); color: var(--tinta-2);
}

/* ── Impressão ──────────────────────────────────────────────────────────
   O sistema já apaga o chão da colmeia, esconde [data-nao-imprimir], clareia
   a célula mata e desfaz a escala de tinta escura dela (globals.css, junto da
   regra que clareia o fundo: as duas metades da mesma correção moram no mesmo
   lugar). Falta o que é do DOCUMENTO: preparo de página, cabeçalho de tabela
   repetido a cada folha e quebras que não partem um bloco no meio. */
@media print{
  @page{ size: A4 portrait; margin: 14mm }
  .laudo{ max-width: none; gap: var(--e4) }
  .laudo .secao, .laudo .grafico, .laudo .opcao, .laudo .celula{ break-inside: avoid; page-break-inside: avoid }
  /* Título sozinho no pé da folha com o capítulo na seguinte é o defeito
     clássico do PDF longo. */
  .laudo .titulo-secao, .laudo .titulo-anexo{ break-after: avoid; page-break-after: avoid }
  .laudo thead{ display: table-header-group }
  .laudo tfoot{ display: table-footer-group }
  .laudo tr{ break-inside: avoid; page-break-inside: avoid }
  .laudo .tabela-meses{ overflow: visible }
  .laudo [data-slot='table-container']{ overflow: visible }
  /* Bloco aninhado no papel: sem preenchimento, só o fio que delimita. */
  .laudo .camada{ background: none; border: 1px solid var(--fio) }
  .laudo .tabela-meses tbody tr[data-equilibrio]{ background: none; font-weight: var(--peso-forte) }
  .laudo .ponto-atual, .laudo .ponto-minimo{ stroke: none }
}
`;
